import {AuthorizationModel} from "../../authentication/models/authorization-model";
import {AppSettings} from "../../app-settings";
import {prop} from "../../decorators/property";
import {JurisdictionModel} from "../../settings/models/jurisdiction";
import {JurisdictionCollection} from "../../settings/collections/jurisdictions";
import {Container} from "aurelia-dependency-injection";
import {backboneModelProperties} from "../../decorators/backbone-properties";
import {AppEvents} from "../../app-events";
import { AuthService } from "../../authentication/auth-service";
import { ContainerHelper } from "../../shared/helpers/container-helper";

@backboneModelProperties({
    urlRoot: AppSettings.ApiUrlPrefix + "usersettings/",
    url: AppSettings.ApiUrlPrefix + "usersettings/",
    relations : [{
        type: Backbone.Many,
        key: "CoreJurisdictions",
        collectionType: JurisdictionCollection
    }],
    defaults : {
        Populated: false,
        AllSelected: false,
        IncludeAllOtherJurisdictions: false,
        IsAuthenticatedByIP: false,
        CoreJurisdictions: [],
        SubscriptionOnly: false
    }
})
export class UserSettingsModel extends AuthorizationModel {

    public static GetSettings() {
        const data = JSON.parse(window.localStorage.getItem(AppSettings.SettingsLocalStorageKey));
        if (data) {
            let jurisdictions = [];
            const cores = data.CoreJurisdictions.filter((j) => j.Selected);
            jurisdictions = jurisdictions.concat(cores);
            const partials = data.CoreJurisdictions.filter((j) => j.Partial || j.Selected);
            for (const partial of partials) {
                if (partial.Children) {
                    jurisdictions = jurisdictions.concat(partial.Children.filter((c) => c.Selected));
                }
            }
            jurisdictions = jurisdictions.map((j) => {
                return {
                    Id : j.Id,
                    Name: j.Name,
                    Priority: j.Priority
                };
            });
            return  {
                CoreJurisdictions: jurisdictions,
                IncludeAllOtherJurisdictions: data.IncludeAllOtherJurisdictions,
                SubscriptionOnly: data.SubscriptionOnly
            };
        }
    }

    @prop() public Populated: boolean;
    @prop() public IncludeAllOtherJurisdictions: boolean;
    @prop() public IsAuthenticatedByIP: boolean;
    @prop() public CoreJurisdictions: Backbone.Collection<JurisdictionModel>;
    @prop() public Section: string;
    @prop() public IsAdminUser: boolean;
    @prop() public SaveOrganisationSettings: boolean;
    @prop() public SubscriptionOnly: boolean;

    private saveCount = 0;
    private authService = ContainerHelper.AuthService;

    constructor(options?) {
        super(options);
    }

    public remove() {
        this.clearLocal();
        this.trigger("destroy", this);
    }

    public setInitialSection(): void {
        this.Section = "jurisdiction";
    }

    public areAllSelected(): boolean {
        const checkboxStates = this.CoreJurisdictions.map((j) => (j.Selected));
        return _.reduce(checkboxStates, (memo: boolean, next: boolean) => memo && next, this.IncludeAllOtherJurisdictions);
    }

    public getJurisdictionsWithChildren(): Backbone.Collection<JurisdictionModel> {
        const allJurisdictions = new Backbone.Collection<JurisdictionModel>();
        this.CoreJurisdictions.each((item) => {
            allJurisdictions.push(item); // push just the parent with no children
            item.Children.each((child) => {
                allJurisdictions.push(child); // push just the children
            });
        });
        return allJurisdictions;
    }

    public getCoreJurisdictions(): Backbone.Collection<JurisdictionModel> {
        return this.CoreJurisdictions;
    }

    public getJurisdictionById(id: string): JurisdictionModel {
        let jurisdiction = this.CoreJurisdictions.find((j) => j.JurisdictionId === parseInt(id));
        if (!jurisdiction) {
            const allJurisdictions = this.getJurisdictionsWithChildren();
            jurisdiction = allJurisdictions.find((j) => j.JurisdictionId === parseInt(id));
        }
        return jurisdiction;
    }

    public get AllJurisdictionsSelected() {
        const allSelected = (this.CoreJurisdictions.all((c) => c.Selected) && this.IncludeAllOtherJurisdictions);
        const noneSelected = (this.CoreJurisdictions.all((c) => !c.Selected && !c.Partial) && !this.IncludeAllOtherJurisdictions);
        return allSelected || noneSelected;
    }

    public loadSettings() {
        return new Promise((resolve, reject) => {
            if (!this.authService.AppUser.IsAuthenticated) {
                resolve();
            } else if (this.Populated) {
                resolve();
             } else if (this.HasLocalStorageData) {
                const localData = this.getLocal();
                this.clear({silent: true}).set(localData);
                resolve();
             } else {
                Promise.resolve(this.fetch()).then(() => {
                    this.saveLocal();
                    resolve();
                });
             }
        });
    }

    public saveAndNotify() {
        this.SaveOrganisationSettings = false;
        this.trigger("jurisdictionsChanged");
        this.saveCount++;
        this.saveLocal();
        return this.saveSettings(false);
    }

    public saveOrganisationSettings() {
        this.SaveOrganisationSettings = true;
        const saveResponse = this.saveSettings(true);
        this.SaveOrganisationSettings = false;
        return saveResponse;
    }

    private saveSettings(organisationSettings: boolean) {
        $("#settings-thinker").fadeIn();
        return this.save(this.attributes, {
            success: (successData) => {
                $("#settings-thinker").fadeOut();
                if (organisationSettings) {
                    ContainerHelper.Events.trigger(AppEvents.InformationMessage, "Organisation settings saved");
                    ContainerHelper.Events.trigger(AppEvents.AppInsightsOrganisationSaved, {Saved: true});
                } else {
                    ContainerHelper.Events.trigger(AppEvents.JurisdictionsSaved);
                    ContainerHelper.Events.trigger(AppEvents.AppInsightsJurisdictionsSaved, {Saved: true});
                }
            },
            error: (model, response) => {
                $("#settings-thinker").fadeOut();
                if (response.status === 500) {
                    ContainerHelper.Events.trigger(AppEvents.ErrorMessage, response.responseJSON.Message);
                } else {
                    if (response.status !== 0) {
                        ContainerHelper.Events.trigger(AppEvents.ErrorMessage, "Could not connect to server");
                    }
                }
            }, parse: false
        });
    }

    public get SelectedJurisdictionIds(): number[] {
        let selectedJurisdictions = [];
        this.CoreJurisdictions.each((j) => {
            selectedJurisdictions = selectedJurisdictions.concat(this.getSelectedIdsForJurisdiction(j));
        });
        return selectedJurisdictions;
    }

    public get AllJurisdictionIds(): number[] {
        let jurisdictions = [];
        this.CoreJurisdictions.each((j) => {
            jurisdictions = jurisdictions.concat(this.getIdsForJurisdiction(j));
        });
        return jurisdictions;
    }

    private getSelectedIdsForJurisdiction(jurisdiction: JurisdictionModel): number[] {
        let ids = [];
        if (jurisdiction.Selected) {
            ids.push(jurisdiction.JurisdictionId);
        }
        if (jurisdiction.HasChildren) {
            const childIds = jurisdiction.Children.each((e) =>
                ids = ids.concat(this.getSelectedIdsForJurisdiction(e))
            );
        }
        return ids;
    }

     private getIdsForJurisdiction(jurisdiction: JurisdictionModel): number[] {
        let ids = [];
        ids.push(jurisdiction.JurisdictionId);
        if (jurisdiction.HasChildren) {
            const childIds = jurisdiction.Children.each((e) =>
                ids = ids.concat(this.getIdsForJurisdiction(e))
            );
        }
        return ids;
    }

    private get HasLocalStorageData() {
        const data = window.localStorage.getItem(AppSettings.SettingsLocalStorageKey);
        return data;
    }

    private saveLocal() {
        window.localStorage.setItem(AppSettings.SettingsLocalStorageKey, JSON.stringify(this.toJSON()));
    }

    private getLocal() {
        const data = JSON.parse(window.localStorage.getItem(AppSettings.SettingsLocalStorageKey));
        return data;
    }

    private clearLocal() {
        window.localStorage.removeItem(AppSettings.SettingsLocalStorageKey);
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/settings/models/user-settings.ts